import { refreshChatAvatar, handleChatGuardrailTruncateRetry, type ChatHost } from "./app-chat.ts";
import {
  activeFleetQuestionForSession,
  fleetTodosForActiveSession,
} from "./app-fleet-interaction.ts";
import { syncLocalLlmPolling } from "./app-polling.ts";
import { runArtifactPreviewsForSession } from "./app-run-artifacts.ts";
import {
  jumpToSessionFileInTranscript,
  noteUploadedSessionFile,
  refreshSessionUploads,
} from "./app-session-uploads.ts";
import { openLocalLlmInFleet } from "./app-settings.ts";
import type { AppViewState } from "./app-view-state.ts";
import type { OpenClawApp } from "./app.ts";
import { attachmentKindForMime } from "./chat/attachment-types.ts";
import type { ChatAssistDisplay } from "./chat/chat-assist-display.ts";
import { showThinkingFromAssistDisplay } from "./chat/chat-assist-display.ts";
import { resolveContextUsage } from "./chat/chat-context-usage.ts";
import { buildChatHistoryGroups } from "./chat/chat-history-catalog.ts";
import { resolveChatLastRunSnapshot } from "./chat/chat-last-run-files.ts";
import { pendingEditMatchesSession } from "./chat/chat-pending-edit.ts";
import { evaluatePlanImplementOffer } from "./chat/chat-plan-implement-offer.ts";
import { canArchiveSessionKey, normalizeArchivedSessionKeys } from "./chat/chat-session-archive.ts";
import { searchActiveChatDeepBatch } from "./chat/chat-thread-search-deep-batch.ts";
import { computeComposerBlocks } from "./chat/composer-reason-channel.ts";
import { resolveFleetQueueMode } from "./chat/fleet-queue-mode.ts";
import { resolveDisplayedChatWorkspacePath } from "./chat/fleet-self-improvement-workspace.ts";
import { extractText } from "./chat/message-extract.ts";
import { fetchSessionFilePreview } from "./chat/session-file-preview.ts";
import { buildSessionFileUrl, uploadSessionFile } from "./chat/session-files.ts";
import { buildSessionSnapshot } from "./chat/session-snapshot.ts";
import {
  computeSessionStatus,
  countOngoingSubagents,
  type SessionStatus,
} from "./chat/session-status.ts";
import { restoreChatSubagentsForSession } from "./chat/subagents-by-session.ts";
import {
  applyClearSelection,
  applySelectAllKeys,
  applyToggleKeySelected,
  applyToggleSelectMode,
  resolveAllSelectableKeys,
} from "./controllers/chat-history-bulk-select.ts";
import { loadChatHistory } from "./controllers/chat.ts";
import { handleChatSendSelfImprovementChange } from "./controllers/fleet-self-improvement-ui.ts";
import { buildLocalLlmViewProps, refreshLocalLlm } from "./controllers/local-llm.ts";
import {
  loadSessionEnvironment,
  type SessionEnvironmentState,
} from "./controllers/session-environment.ts";
import { isFleetConsoleTab } from "./navigation.ts";
import { resolveOperatorTrustForSession } from "./operator-trust/state.ts";
import { loadFavoriteChats, toggleFavoriteChat } from "./storage.ts";
import type { ChatProps } from "./views/chat.ts";
import { syncChatComposeDraft } from "./views/chat.ts";
import type { FleetUnifiedDrawerTab } from "./views/fleet-unified-layout.ts";
import {
  fleetMainTabToInspectorTab,
  resetFleetWorkspaceLayout,
  type FleetInspectorTabId,
  type FleetWorkspaceLayoutState,
  type FleetWorkspacePreset,
} from "./views/fleet-workspace-presets.ts";

export type FleetChatRenderContext = {
  chatDisabledReason: string | null;
  chatFocus: boolean;
  assistDisplay: ChatAssistDisplay;
  assistantAvatarUrl: string | null;
};

/**
 * UX-PLAN Pillar F — typed reason channel. `buildFleetChatRenderProps`
 * computes a `ComposerBlock[]` from the live state (connected, draft,
 * attachments, context usage, session row) so the view can render one
 * `.callout` per gate. Empty when the composer is ready to send.
 */
function buildSidebarLoadedMessageTextIndex(
  cache: ReadonlyMap<string, unknown[]>,
): Map<string, string> {
  // JAISIU-3651 — local mirror of `buildLoadedMessageTextIndex` from
  // `views/fleet-chat-history-sidebar.ts`. Lives here so the render
  // pipeline can build the index from the host's private
  // `chatMessagesBySession` map without leaking internal types through
  // the view's import graph. Behaviour is identical: walks every
  // session's loaded batch, joins `extractText` per message, and
  // returns a lowercased single-string map keyed by session key. The
  // sidebar short-circuits to title+preview when this map is empty.
  const out = new Map<string, string>();
  for (const [sessionKey, messages] of cache) {
    if (!Array.isArray(messages) || messages.length === 0) {
      continue;
    }
    const parts: string[] = [];
    for (const msg of messages) {
      const text = extractText(msg as Parameters<typeof extractText>[0]);
      if (text) {
        parts.push(text);
      }
    }
    if (parts.length > 0) {
      out.set(sessionKey, parts.join("\n").toLowerCase());
    }
  }
  return out;
}

function computeFleetChatComposerBlocks(state: AppViewState) {
  const hasAttachments = (state.chatAttachments?.length ?? 0) > 0;
  const attachmentsUploading = Boolean(
    state.chatAttachments?.some((a) => (a as { uploading?: boolean }).uploading),
  );
  const activeSession = state.sessionsResult?.sessions?.find((row) => row.key === state.sessionKey);
  const usage = resolveContextUsage(activeSession, state.sessionsResult?.defaults, {
    messages: state.chatMessages,
    pending: state.chatSending,
  });
  return computeComposerBlocks({
    connected: state.connected,
    canSend: state.connected,
    draft: state.chatMessage,
    hasAttachments,
    attachmentsUploading,
    contextPercent: usage.percent,
    wrongSession: false,
  });
}

/**
 * JAISIU-3651 — ensure chats that already match by title/preview in
 * the current sidebar catalog have at least one `chat.history` batch
 * in the local cache, so loaded message bodies become searchable.
 *
 * Walks the already-built `ChatHistoryEntry[]` (title + preview are
 * the exact strings the sidebar renders), picks rows whose title or
 * preview already contains the query AND whose session key isn't
 * already cached, and issues one `chat.history({ sessionKey, limit: 50 })`
 * per row. The active tab stays unchanged — we never re-target
 * `state.sessionKey` — so this does NOT swap the chat-panel view.
 *
 * Best-effort: errors are swallowed because the next keystroke will
 * retry for any keys still missing from the cache.
 */
async function loadMissingChatBatchesForSidebarSearch(
  app: OpenClawApp,
  query: string,
  candidates: ReadonlyArray<{ key: string; title: string; preview: string }>,
): Promise<void> {
  if (!app.client || !app.connected) {
    return;
  }
  const q = query.trim().toLowerCase();
  if (!q || candidates.length === 0) {
    return;
  }
  const cache = app.getChatMessagesBySessionForSidebar();
  const targets: string[] = [];
  for (const row of candidates) {
    if (typeof row.key !== "string" || !row.key.trim()) {
      continue;
    }
    if (cache.has(row.key)) {
      continue;
    }
    const title = typeof row.title === "string" ? row.title.toLowerCase() : "";
    const preview = typeof row.preview === "string" ? row.preview.toLowerCase() : "";
    if (title.includes(q) || preview.includes(q)) {
      targets.push(row.key);
    }
  }
  if (targets.length === 0) {
    return;
  }
  // Cap in-flight RPCs so a fast typist never queues a hundred parallel
  // `chat.history` requests. 4 is well below the gateway's per-conn
  // limit and keeps the UI responsive while still warming the cache.
  const CONCURRENCY = 4;
  for (let i = 0; i < targets.length; i += CONCURRENCY) {
    const slice = targets.slice(i, i + CONCURRENCY);
    await Promise.all(
      slice.map(async (key) => {
        try {
          await app.client?.request("chat.history", { sessionKey: key, limit: 50 });
        } catch {
          // Best-effort — try again on the next keystroke.
        }
      }),
    );
  }
}

export function buildFleetChatRenderProps(
  state: AppViewState,
  ctx: FleetChatRenderContext,
): ChatProps {
  const app = state as unknown as OpenClawApp;
  const composerBlocks = computeFleetChatComposerBlocks(state);
  const sessionKey = state.sessionKey.trim();
  const planRunActive =
    Boolean(state.chatRunId) ||
    state.chatSending ||
    state.chatStream !== null ||
    (state.sessionBackgroundRunIds?.length ?? 0) > 0;
  // UX-PLAN Pillar D — single SessionSnapshot for the inspector + thread.
  // Computed here (once per render) so both downstream consumers read the
  // same value. Invariant: `snapshot.children.length === subagentsBySession.get(active).length`
  // — `assertSessionSnapshotInvariant` logs in dev when the two diverge.
  const sessionSnapshot = buildSessionSnapshot({
    sessionKey: state.sessionKey,
    sessions: state.sessionsResult,
    tabMeta: state.chatTabMeta,
    subagentCardHost: app,
    chatSubagents: state.chatSubagents,
    environment: state.sessionEnvironment,
    continuity: state.chatPalaceContinuity,
    contextBreakdown: state.chatContextBreakdown,
    lastError: state.lastError,
    runActive: planRunActive,
    backgroundRunsActive: (state.sessionBackgroundRunIds?.length ?? 0) > 0,
    compactionActive: Boolean(state.compactionStatus?.active),
    fleetQuestionPending:
      activeFleetQuestionForSession(state)?.sessionKey === state.sessionKey.trim(),
    connected: state.connected,
    lastEventAtMs: state.chatStreamStartedAt ?? null,
    nowMs: state.chatWaitNow ?? Date.now(),
  });
  const planImplementEval = evaluatePlanImplementOffer({
    sessionKey: state.sessionKey,
    composerMode: state.chatComposerMode,
    messages: state.chatMessages ?? [],
    runActive: planRunActive,
    dismissedFingerprint: sessionKey
      ? (state.planImplementDismissedFingerprint[sessionKey] ?? null)
      : null,
  });
  const archivedKeys = normalizeArchivedSessionKeys(state.settings.chatArchivedSessionKeys);
  // Per spec: chat-history is a navigator over saved conversation sessions.
  // The agent context is recovered on click (the session key encodes it), so
  // the sidebar does not need to filter by it.
  //
  // Pillar A — Session is a first-class object (Gap 1). We layer
  // per-session client signals (status, subagentCount) on top of the
  // row-derived defaults so the sidebar can render the status chip +
  // subagent count chip without re-deriving from raw state per row.
  //
  //   - The active session uses `state.chatSubagents` directly (the
  //     registry-synced live list).
  //   - Non-active sessions fall back to `cardsFromSpawnedSessions(key,
  //     sessionsResult)` which walks the catalog and reconstructs cards
  //     from spawned subagent sessions — no new in-memory cache, no
  //     stale snapshotting.
  //
  // Pure module: `computeSessionStatus` + `countOngoingSubagents` are
  // deterministic over the input. The whole pass is O(sessions *
  // subagents-per-session), which is bounded by the catalog size.
  const sessionsList = state.sessionsResult?.sessions ?? [];
  const sessionMeta: Record<string, { status: SessionStatus; subagentCount: number }> = {};
  const activeCards = state.chatSubagents ?? [];
  for (const row of sessionsList) {
    const key = row.key?.trim();
    if (!key) {
      continue;
    }
    const cards =
      key === state.sessionKey.trim()
        ? activeCards
        : restoreChatSubagentsForSession(app, key, state.sessionsResult);
    const subagentCount = countOngoingSubagents(cards);
    const status = computeSessionStatus({
      subagents: cards,
      abortedLastRun: row.abortedLastRun === true,
      connected: state.connected,
    });
    sessionMeta[key] = { status, subagentCount };
  }
  const history = buildChatHistoryGroups({
    activeSessionKey: state.sessionKey,
    openTabs: app.getChatOpenTabs?.() ?? state.settings.chatOpenTabs,
    archivedKeys,
    sessions: state.sessionsResult,
    includeArchived: app.chatHistoryShowArchived,
    sessionMeta,
  });
  // JAISIU-3651 — flatten the sidebar's view of the catalog into a
  // minimal candidate list (`{ key, title, preview }`) that both the
  // render prop and the body-batch loader can share. Title/preview
  // here come from the same `rowToEntry` path the sidebar renders, so
  // the body-loader's "which chats already match by title/preview?"
  // predicate agrees exactly with what the operator sees.
  const sidebarSearchCandidates: Array<{ key: string; title: string; preview: string }> = [];
  for (const group of history.active) {
    for (const entry of group.entries) {
      sidebarSearchCandidates.push({ key: entry.key, title: entry.title, preview: entry.preview });
    }
  }
  for (const entry of history.archived) {
    sidebarSearchCandidates.push({ key: entry.key, title: entry.title, preview: entry.preview });
  }

  // JAISIU-3327 / UX-R2 — derive the all-visible-keys list and push it
  // back onto the App so the sidebar's "Select all (N)" counter and
  // "Delete selected" gate always reflect what the operator sees right
  // now (groups + archived, in catalog order). The cached row snapshot
  // powers the bulk-delete confirm modal so it can show titles + skip
  // the main session.
  app.setChatHistoryAllSelectableKeys(resolveAllSelectableKeys(history.active, history.archived));
  app.chatHistoryBulkDeleteRows = state.sessionsResult ? [...state.sessionsResult.sessions] : [];

  return {
    sessionKey: state.sessionKey,
    onSessionKeyChange: (next) => {
      state.activateChatSession(next);
    },
    chatOpenTabs: state.getChatOpenTabs(),
    onSelectChatTab: (next) => state.activateChatSession(next),
    onCloseChatTab: (key) => state.closeChatTab(key),
    onNewChatTab: () => void state.handleNewEmptyChatTab(),
    onForkChatTab: () => void state.handleForkCurrentChatTab(),
    onResetChatTab: () => void state.handleResetChatTab(),
    hello: state.hello,
    employees: state.employeesList ?? [],
    agentsList: state.agentsList?.agents ?? [],
    modelCatalog: state.chatModelCatalog,
    modelCatalogLoading: state.chatModelCatalogLoading,
    modelCatalogError: state.chatModelCatalogError,
    modelPatching: state.chatModelPatching,
    onSelectChatTarget: (key) => state.handleChatTargetChange(key),
    onSelectChatModel: (modelRef) => void state.handleChatModelChange(modelRef),
    composerMode: state.chatComposerMode,
    onComposerModeChange: (mode) => state.handleChatComposerModeChange(mode),
    thinkingLevel: state.chatThinkingLevel,
    assistDisplay: ctx.assistDisplay,
    showThinking: showThinkingFromAssistDisplay(ctx.assistDisplay),
    loading: state.chatLoading,
    transcriptLoading: state.chatTranscriptLoading,
    // JAISIU-XXXX — hydration-mode crossfade. `cold` = skeleton + hairline,
    // `warm` = cached transcript paints immediately, `idle` = stable. The
    // outgoing snapshot is kept rendered until the cold payload arrives so
    // the pane never blanks on a session switch.
    hydrationMode: state.chatHydrationMode ?? "idle",
    hydrationOutgoing: state.chatHydrationOutgoing ?? null,
    historyHydrationError: state.chatHistoryHydrationError ?? null,
    onRetryHistoryHydration: () => state.retryHistoryHydration?.(),
    scrollHighlightMessageId: state.chatScrollHighlightMessageId ?? null,
    sending: state.chatSending,
    // JAISIU-3327 — collect-mode queue depth. Read at render time; the
    // controller mutates this on every sendChatMessage and on chat.final,
    // so the value is always in sync with the latest optimistic state.
    // The composer renders a calming "Queued (n)" line whenever this is
    // > 0, and the per-bubble chip picks up the flag from each message.
    collectQueueDepth: state.chatCollectQueueDepth ?? 0,
    compactionStatus: state.compactionStatus,
    assistantAvatarUrl: ctx.assistantAvatarUrl,
    messages: state.chatMessages,
    toolMessages: state.chatToolMessages,
    stream: state.chatStream,
    reasoningStream: state.chatReasoningStream,
    // Fleet Control staleness pill — read at render time; the 1s ticker
    // triggers requestUpdate when the verdict is non-fresh.
    chatSync: state.chatSync ?? null,
    chatRunIdForPill: state.chatRunId ?? null,
    chatSendingForPill: state.chatSending === true,
    chatStreamStartedAtForPill: state.chatStreamStartedAt ?? null,
    liveRunHint: state.chatLiveRunHint,
    stickyLiveRunHint: state.stickyLiveRunHintBySession[sessionKey] ?? null,
    chatStopToast: state.chatStopToastBySession?.[sessionKey] ?? null,
    chatActiveFileToolPath: state.chatActiveFileToolPath,
    runArtifactPreviews: runArtifactPreviewsForSession(app, sessionKey),
    onRunArtifactFileClick: (changeId, path, sessionKey) =>
      state.handleOpenChatFileDiff(changeId, path, sessionKey),
    onFileChangesFileClick: (changeId, path, sessionKey) =>
      state.handleOpenChatFileDiff(changeId, path, sessionKey),
    chatFileDiffOpen: state.chatFileDiffOpen,
    chatFileDiffDetail: state.sessionArtifactDetail,
    chatFileDiffDetailLoading: state.sessionArtifactDetailLoading,
    chatFileDiffError: state.sessionArtifactsError,
    chatFileDiffView: state.chatFileDiffView,
    onChatFileDiffViewChange: (view) => state.handleChatFileDiffViewChange(view),
    onCloseChatFileDiff: () => state.handleCloseChatFileDiff(),
    chatFileDiffSplitView: state.chatFileDiffSplitView,
    onChatFileDiffSplitViewChange: (next) => state.handleChatFileDiffSplitViewChange(next),
    chatPendingReviewChangeIds: state.chatPendingReviewChangeIds,
    chatLastRunFiles: resolveChatLastRunSnapshot(state, sessionKey)?.files,
    chatLastRunTotals: resolveChatLastRunSnapshot(state, sessionKey)?.totals,
    onComposerReviewAll: () => state.handleComposerReviewAll(),
    onComposerKeepAll: () => state.handleComposerKeepAll(),
    onComposerUndoAll: () => void state.handleComposerUndoAll(),
    fleetComposerFileReviewCollapsed: state.settings.fleetComposerFileReviewCollapsed,
    onToggleFleetComposerFileReviewCollapsed: () => {
      state.applySettings({
        ...state.settings,
        fleetComposerFileReviewCollapsed: !state.settings.fleetComposerFileReviewCollapsed,
      });
    },
    chatComposerFileReviewError: state.chatComposerFileReviewError,
    chatArtifactReviewBusy: state.chatArtifactReviewBusy,
    onAcceptArtifactChange: (changeId) => state.handleAcceptArtifactChange(changeId),
    onRejectArtifactChange: (changeId) => void state.handleRejectArtifactChange(changeId),
    onOpenArtifactInProjectFiles: (path, sessionKey) =>
      state.handleOpenArtifactInProjectFiles(path, sessionKey),
    chatActivityPanelCollapsed: state.chatActivityPanelCollapsed,
    onToggleChatActivityPanelCollapsed: () => state.handleToggleChatActivityPanelCollapsed(),
    // JAISIU-3473 — upward-pagination wiring. `loadOlderChatHistory`
    // is the new cursor-based scroll-up pager; the legacy
    // `loadMoreChatHistory` (which bumped `chatHistoryDisplayLimit`
    // to widen the server-side slice) is gone.
    chatHistoryDisplayLimit: state.chatHistoryDisplayLimit,
    onLoadOlderChatHistory: () => void state.loadOlderChatHistory(),
    onLoadMoreChatHistory: () => void state.loadOlderChatHistory(), // legacy alias
    // JAISIU-3532 — newer-direction callback mirror.
    onLoadNewerChatHistory: () => void state.loadNewerChatHistory(),
    chatMessageSearchQuery: state.chatMessageSearchQuery,
    onChatMessageSearchQueryChange: (next) => {
      // JAISIU-3655 — the host owns the index reset + DOM
      // highlight pass. Forwarding here means the chat view does
      // NOT have to know about host state — it just renders the
      // counter off the props we forward back.
      state.handleChatMessageSearchQueryChange(next);
      // JAISIU-3656 — when a search query is entered, the view's
      // in-thread filter only sees the currently rendered DOM.
      // Older matches (behind `chatHistoryOldestLoadedId`) would
      // be invisible. Run the deep-batch walker: it pages
      // `chat.history` with `before=<cursor>` until either matches
      // resolve, the server says no more older content, or the
      // per-query page cap is hit. The walker NEVER writes to
      // `chatMessagesBySession` — older pages live only in the
      // walker's ephemeral index. Only `onChatMessageSearchNav`
      // (when the operator navigates to a deep match) triggers a
      // `loadOlderChatHistory` call that prepends to the rendered
      // window.
      if (next.replace(/\s+/g, "").length > 0) {
        void searchActiveChatDeepBatch(state, next).then((result) => {
          // Forward the resolved total into the host's reactive
          // counter so the UI reflects live + deep matches without
          // a separate subscription.
          state.handleChatMessageDeepBatchResult(result);
        });
      }
    },
    chatMessageSearchTotal: state.chatMessageSearchTotal,
    chatMessageSearchCurrentIndex: state.chatMessageSearchCurrentIndex,
    onChatMessageSearchNav: (direction) => {
      state.handleChatMessageSearchNav(direction);
    },
    // JAISIU-3655 (followup) — operator toggle surfaced through
    // the gear-icon popover next to the in-thread nav. Default
    // OFF (false). The toggle gates whether the highlight pass
    // walks into the assistant-turn activity section
    // (`[data-testid="chat-turn-activity"]`).
    chatMessageSearchInActivities: app.chatMessageSearchInActivities === true,
    onChatMessageSearchInActivitiesChange: (next: boolean) => {
      // JAISIU-3655 (followup) — the in-thread toggle and the
      // sidebar toggle are TWO views of ONE boolean. Flipping
      // one must update the other (and both must reflect the new
      // value on the next paint). We assign both `@state()`
      // fields; Lit triggers a single re-render for the
      // shared update cycle.
      app.chatMessageSearchInActivities = next;
      app.chatHistorySearchInActivities = next;
    },
    chatFileContextIndex: state.chatFileContextIndex,
    onChatFileContextIndexChange: (next) => {
      state.chatFileContextIndex = next;
    },
    onFileContextSelect: (path) => state.handleFileContextSelect(path),
    chatFolderContextIndex: state.chatFolderContextIndex,
    onChatFolderContextIndexChange: (next) => {
      state.chatFolderContextIndex = next;
    },
    onFolderContextSelect: (path) => state.handleFolderContextSelect(path),
    fleetTodosBusy: state.fleetTodosBusy,
    onFleetTodoStatusChange: (id, status) => void state.handleFleetTodoStatusChange(id, status),
    chatCheckpoints: state.chatCheckpoints,
    chatCheckpointsLoading: state.chatCheckpointsLoading,
    chatCheckpointsCollapsed: state.chatCheckpointsCollapsed,
    onToggleChatCheckpointsCollapsed: () => state.handleToggleChatCheckpointsCollapsed(),
    // JAISIU-3551 — avatar-driven manual collapse state for chat bubbles.
    collapsedMessageIds: state.chatCollapsedMessageIds,
    onToggleMessageCollapsed: (messageId) => state.handleToggleChatCollapsedMessage(messageId),
    onRefreshCheckpoints: () => void state.refreshChatCheckpoints(),
    onRestoreCheckpoint: (sinceTs) => void state.handleRestoreCheckpoint(sinceTs),
    streamStartedAt: state.chatStreamStartedAt,
    injectedAt: state.chatInjectedAt ?? null,
    chatWaitNow: state.chatWaitNow,
    draft: state.chatMessage,
    queue: state.chatQueue,
    connected: state.connected,
    canSend: state.connected,
    disabledReason: ctx.chatDisabledReason,
    error: state.lastError,
    onDismissError: () => {
      state.lastError = null;
    },
    onGuardrailTruncateRetry: (trimmed) => {
      // Pillar G — re-send the trimmed message. The chat-guardrail-banner
      // hands us the already-truncated string; we drop it into the
      // composer, clear the error, and re-issue send.
      void handleChatGuardrailTruncateRetry(state as ChatHost, trimmed);
    },
    sessions: state.sessionsResult,
    focusMode: ctx.chatFocus,
    transcriptMode: state.settings.chatTranscriptMode,
    onToggleTranscriptMode: () => {
      if (state.onboarding) {
        return;
      }
      state.applySettings({
        ...state.settings,
        chatTranscriptMode: !state.settings.chatTranscriptMode,
      });
    },
    reasoningExpanded: state.settings.chatReasoningExpanded,
    onToggleReasoningExpanded: () => {
      state.applySettings({
        ...state.settings,
        chatReasoningExpanded: !state.settings.chatReasoningExpanded,
      });
    },
    onRefresh: () => {
      state.resetToolStream();
      return Promise.all([loadChatHistory(state), refreshChatAvatar(state)]);
    },
    // Focus-mode entry was removed in JAISIU-3044; the prop stays no-op so
    // the chat.ts render contract is preserved.
    onToggleFocusMode: () => undefined,
    onSetAssistDisplay: (display) => {
      state.applySettings({
        ...state.settings,
        chatAssistDisplay: display,
      });
    },
    onChatScroll: (event) => state.handleChatScroll(event),
    // JAISIU-3474 — the scroll handler captures the topmost visible
    // message id and the host schedules a debounced IDB save so the
    // next refresh can restore mid-thread scroll position.
    scheduleScrollAnchorSave: () => state.scheduleScrollAnchorSave(),
    chatScrollAnchor: state.chatScrollAnchor,
    chatScrollRestorationPending: state.chatScrollRestorationPending,
    pinnedUserQuestion: state.chatPinnedUserQuestion,
    // JAISIU-3320 / Pillar I — pinned active job banner props. Driven
    // by `state.settings.pinnedActiveJob`; the renderer ignores the
    // prop when the pinned session matches the active tab.
    pinnedActiveJob: state.settings.pinnedActiveJob ?? null,
    pinnedActiveJobLabel: (() => {
      const key = state.settings.pinnedActiveJob;
      if (!key) return null;
      const row = state.sessionsResult?.sessions?.find((r) => r.key === key);
      const label = row?.label?.trim() || row?.displayName?.trim();
      if (label) return label;
      return key;
    })(),
    pinnedActiveJobStatus: (() => {
      const key = state.settings.pinnedActiveJob;
      if (!key || key !== state.sessionKey.trim()) return null;
      const cards = state.chatSubagents ?? [];
      const total = cards.length;
      const settled = cards.filter((c) => {
        const s = c.status;
        return s === "done" || s === "error" || s === "cancelled";
      }).length;
      if (total === 0) return null;
      if (settled >= total) return "all settled";
      return `${settled} of ${total} settled`;
    })(),
    onActivatePinnedActiveJob: () => {
      const key = state.settings.pinnedActiveJob;
      if (key) state.activateChatSession(key);
    },
    onDismissPinnedActiveJob: () => {
      state.setPinnedActiveJob(null);
    },
    fleetSendAck: state.fleetSendAck,
    onDismissFleetSendAck: () => {
      state.fleetSendAck = null;
    },
    onDraftChange: (next) => {
      state.chatMessage = next;
      if (state.fleetSendAck) {
        state.fleetSendAck = null;
      }
      // JAISIU-3503 — drop any "Run stopped" toast the moment the
      // operator begins typing the next prompt. Otherwise the toast
      // lingers across the draft edit and crowds the composer.
      if (next.length > 0) {
        state.clearRunStoppedToast(state.sessionKey);
      }
      state.chatMentionIndex = 0;
      state.chatSlashCommandIndex = 0;
      // Send options (orchestrate with @all, create new project, self
      // improvement) are user-controlled only. We intentionally do NOT
      // auto-detect them from draft keywords — that unexpectedly forks the
      // main chat and jumps the operator away from the session they are on.
    },
    chatSendOrchestrateAll: state.chatSendOrchestrateAll,
    onChatSendOrchestrateAllChange: (next) => {
      state.writeChatSendOption("orchestrateWithAll", next);
    },
    chatSendCreateNewProject: state.chatSendCreateNewProject,
    onChatSendCreateNewProjectChange: (next) => {
      state.writeChatSendOption("createNewProject", next);
      if (next) {
        state.chatSendProjectId = null;
      }
    },
    chatSendSelfImprovement: state.chatSendSelfImprovement,
    chatSelfImprovementRepoWorkspace: state.chatSelfImprovementRepoWorkspace,
    chatSessionWorkspacePath: state.chatWorkspacePath,
    onChatSendSelfImprovementChange: (next) => {
      void handleChatSendSelfImprovementChange(state, next);
    },
    chatSendProjectId: state.chatSendProjectId,
    onChatSendProjectIdChange: (projectId) => {
      state.chatSendProjectId = projectId;
      if (projectId) {
        state.chatSendCreateNewProject = false;
      }
    },
    agents: state.agentsList?.agents ?? [],
    mentionIndex: state.chatMentionIndex,
    onMentionIndexChange: (next) => (state.chatMentionIndex = next),
    onMentionSelect: (agentId) => {
      // JAISIU-3588 (followup) — when the user picks an agent
      // from the mention picker, replace the trailing `@token`
      // (the one the picker was filtering) with `@agentId ` so
      // the user's preceding text is preserved.
      const replacement = `@${agentId} `;
      const prev = state.chatMessage;
      // Strip the last @token from prev and append the
      // replacement.
      const stripped = prev.replace(/(^|\s)@[A-Za-z0-9._-]*$/, (_match, lead) => lead);
      const next = stripped + replacement;
      state.chatMessage = next;
      state.chatMentionIndex = 0;
      syncChatComposeDraft(next);
    },
    slashCommandIndex: state.chatSlashCommandIndex,
    onSlashCommandIndexChange: (next) => (state.chatSlashCommandIndex = next),
    onSlashCommandSelect: (next) => {
      // JAISIU-3588 (followup) — replace the trailing /token
      // (the one the picker was filtering) with the picked
      // command so the user's preceding text is preserved.
      const prev = state.chatMessage;
      const stripped = prev.replace(/\s*\/[A-Za-z0-9._-]*$/, "").replace(/\s+$/, "");
      const merged = stripped ? `${stripped} ${next}` : next;
      state.chatMessage = merged;
      state.chatSlashCommandIndex = 0;
      syncChatComposeDraft(merged);
      requestAnimationFrame(() => {
        document.querySelector<HTMLTextAreaElement>(".chat-compose--fleet textarea")?.focus();
      });
    },
    // JAISIU-types-sweep: `state.chatAttachments` carries the
    // narrow `chat/attachment-types.ts::ChatAttachment` while the
    // chat-thread renderer still imports the wide shape from
    // `ui-types.ts`. Both shapes are structurally compatible (the
    // narrow shape carries the wide's optional fields), so cast
    // once at the boundary. Same for the live reader.
    attachments: state.chatAttachments as unknown as never,
    onAttachmentsChange: (next) =>
      (state.chatAttachments = next as unknown as typeof state.chatAttachments),
    // Live reader for the async upload pipeline. `attachments` above is a
    // snapshot frozen at render time; the pipeline awaits each store call and
    // must not merge against it.
    getAttachments: () => state.chatAttachments as unknown as never,
    onAttachmentUpload: async (file) => {
      const origin = state.client?.getHttpOrigin?.() ?? "";
      // Same auth as the chat frames: the shared token from Settings
      // (#token=… URL arg → cookie/storage). No per-device JWT.
      const token = state.client?.token?.();
      const sessionKey = state.sessionKey ?? "";
      if (!sessionKey) {
        return null;
      }
      try {
        const stored = await uploadSessionFile({ origin, token, file, sessionKey });
        // Seed the Files tab immediately: the operator's own upload must be
        // visible without a Refresh click.
        noteUploadedSessionFile(state, sessionKey, stored);
        return {
          sessionFileId: stored.fileId,
          filename: stored.filename,
          mediaType: stored.mediaType,
          size: stored.size,
          kind: stored.kind,
          // The gateway serves the real derivative. Only pass a preview URL
          // when it actually generated one — a `<img>` pointed at a 404
          // renders as the near-black box from the recording.
          ...(stored.previewMediaType
            ? {
                previewUrl: buildSessionFileUrl({
                  origin,
                  sessionKey,
                  fileId: stored.fileId,
                  variant: "preview" as const,
                }),
              }
            : {}),
        };
      } catch (err) {
        // Deliberately NOT `state.lastError`. A refused file is marked on its
        // own chip by the composer; a banner here would tell the operator to
        // retry a whole message over one bad attachment.
        console.warn("[chat] session file upload failed", err);
        throw err;
      }
    },
    // Files tab: inbound uploads above project changes.
    sessionUploads: {
      loading: state.sessionUploads.loading,
      error: state.sessionUploads.error,
      files: state.sessionUploads.files,
    },
    onRefreshSessionUploads: () => void refreshSessionUploads(state),
    onSessionUploadJump: (fileId) => jumpToSessionFileInTranscript(state, fileId),
    // Transcript rows link to the same stored bytes the model was given.
    // Resolution lives here because this module owns the HTTP origin and
    // the Control-UI base path.
    //
    // JAISIU-4435 — `disposition=inline` so the preview overlay's PDF
    // `<iframe>` renders the document instead of triggering a browser
    // download (the gateway would otherwise answer `attachment` for
    // anything it does not consider renderable).
    resolveSessionFileUrl: (file) =>
      buildSessionFileUrl({
        origin: state.client?.getHttpOrigin?.() ?? "",
        sessionKey: state.sessionKey ?? "",
        fileId: file.fileId,
        variant: "raw",
        disposition: "inline",
      }),
    // JAISIU-4148 — separate resolver for the preview/thumbnail
    // derivative so the chip uses the URL the gateway actually
    // returned (not a /raw→/preview string-replace workaround that
    // breaks on signed/CDN URL shapes). The raw URL still powers the
    // click-through into the full-resolution overlay.
    resolveSessionFilePreviewUrl: (file) =>
      buildSessionFileUrl({
        origin: state.client?.getHttpOrigin?.() ?? "",
        sessionKey: state.sessionKey ?? "",
        fileId: file.fileId,
        variant: "preview",
      }),
    // JAISIU-4449 — read the *prepared preview* of a stored session
    // file: the tabular schema (column names, row count, sample rows)
    // for a spreadsheet, the extracted text for a document.
    //
    // The gateway already prepared those derivatives when the file was
    // stored, so this reads them — it does not re-parse the original
    // bytes in the browser. When the gateway has nothing to show, the
    // overlay keeps its honest "not analysed" message: the loader
    // returns `null` and never fabricates columns or an empty body.
    loadSessionFilePreview: async (file) => {
      const origin = state.client?.getHttpOrigin?.() ?? "";
      const token = state.client?.token?.();
      const sessionKey = state.sessionKey ?? "";
      if (!sessionKey || !file.fileId) {
        return null;
      }
      const result = await fetchSessionFilePreview({
        origin,
        token,
        sessionKey,
        fileId: file.fileId,
      });
      if (!result.available) {
        return null;
      }
      return {
        id: file.fileId,
        messageId: file.fileId,
        kind: attachmentKindForMime(file.mediaType),
        mime: file.mediaType,
        filename: file.filename,
        sizeBytes: file.size,
        ...(result.tabular ? { tabular: result.tabular } : {}),
        ...(result.text ? { text: result.text } : {}),
        // JAISIU-4479 — the structured document blocks (headings, lists,
        // paragraphs, tables) when the gateway extracted them. Additive:
        // the flat text above is still there, so the document body has
        // both a structured view and a plain fallback.
        ...(result.blocks ? { blocks: result.blocks } : {}),
      };
    },
    // Inline-preview actions (JAISIU-4018 followup). The text is in
    // the operator's hand at this point — we don't re-call any model,
    // we just put the prompt in the composer and let them edit + send.
    onAttachmentSummarize: (file, text) => {
      const head = text.length > 8_000 ? `${text.slice(0, 8_000)}…` : text;
      const next = `Summarize the attached ${file.filename}:\n\n${head}`;
      state.chatMessage = next;
    },
    onAttachmentUseInComposer: (file, text) => {
      const head = text.length > 16_000 ? `${text.slice(0, 16_000)}…` : text;
      const next = `${state.chatMessage ?? ""}${state.chatMessage ? "\n\n" : ""}— ${file.filename} —\n${head}`;
      state.chatMessage = next;
    },
    resolveSessionFileVariantUrl: (file, variant) =>
      buildSessionFileUrl({
        origin: state.client?.getHttpOrigin?.() ?? "",
        sessionKey: state.sessionKey ?? "",
        fileId: file.fileId,
        variant,
      }),
    onSend: () => state.handleSendChat(),
    canAbort: Boolean(state.chatRunId),
    onAbort: () => void state.handleAbortChat(),
    // JAISIU-3297 UX-R8 (operator feedback 2026-09-14) — Stop and Stop
    // thinking now share a single button at the chat-send-button slot.
    // The destructive Shift-click handler is wired here; the view
    // exposes it only when the run is in flight, mirroring `canAbort`.
    onStopThinking: () => void state.handleStopThinking(),
    onQueueRemove: (id) => state.removeQueuedMessage(id),
    onQueueEdit: (id) => state.editQueuedMessage(id),
    onEditUserMessage: (rewindTarget, draft) =>
      void state.handleEditUserMessage(rewindTarget, draft),
    chatEditingPriorMessage: pendingEditMatchesSession(
      app.chatPendingMessageEdit,
      state.sessionKey,
    ),
    chatPendingRewindTarget: pendingEditMatchesSession(app.chatPendingMessageEdit, state.sessionKey)
      ? app.chatPendingMessageEdit.rewindTarget
      : null,
    onCancelPendingMessageEdit: () => state.handleCancelPendingMessageEdit(),
    planImplementShow: planImplementEval.show,
    planImplementPreview: planImplementEval.planPreview,
    planImplementFingerprint: planImplementEval.fingerprint,
    onImplementPlan: () => void state.handleImplementPlan(),
    onDismissPlanImplement: () => {
      if (planImplementEval.fingerprint) {
        state.dismissPlanImplementOffer(planImplementEval.fingerprint);
      }
    },
    fleetQuestion: activeFleetQuestionForSession(state),
    fleetQuestionDraft: state.fleetQuestionDraft,
    fleetQuestionFreeText: state.fleetQuestionFreeText,
    fleetQuestionBusy: state.fleetQuestionBusy,
    fleetQuestionError: state.fleetQuestionError,
    fleetQuestionLastAnswer: state.fleetQuestionLastAnswer,
    onFleetQuestionToggleOption: (questionId, optionId, allowMultiple) =>
      state.handleFleetQuestionToggleOption(questionId, optionId, allowMultiple),
    onFleetQuestionFreeTextChange: (questionId, value) =>
      state.handleFleetQuestionFreeTextChange(questionId, value),
    onFleetQuestionSubmit: () => void state.handleFleetQuestionSubmit(),
    fleetTodos: fleetTodosForActiveSession(state),
    fleetTodosStripCollapsed: state.fleetTodosStripCollapsed,
    onToggleFleetTodosStripCollapsed: () => state.handleToggleFleetTodosStripCollapsed(),
    onRegenerateAssistant: () => void state.handleRegenerateAssistant(),
    chatMainRun: state.chatMainRun,
    chatMainRunExpanded: state.chatMainRunExpanded,
    onToggleMainRunExpand: () => state.toggleMainRunExpand(),
    onInsertIntoComposer: (text) => state.handleInsertIntoComposer(text),
    fleetDefaultQueueMode: resolveFleetQueueMode(
      state.settings.fleetDefaultQueueModeBySession,
      state.settings.fleetDefaultQueueMode,
      state.sessionKey,
    ),
    onQueueModeSelect: (mode) => state.handleQueueModeSelect(mode),
    // JAISIU-3503: a SINGLE scroll-nav chevron whose direction is
    // computed by the controller in app-scroll.ts. The controller
    // owns the visible/armed state machine; this view only renders
    // the icon + click target. When the operator is reading mid-
    // thread and scrolls up, the chevron flips to ↑ (jump to top).
    // When they scroll down (or a new message arrives below the
    // fold), the chevron flips to ↓ (jump to bottom).
    chatScrollNavDirection: state.chatNewMessagesAbove
      ? "up"
      : state.chatNewMessagesBelow
        ? "down"
        : undefined,
    onScrollToTop: () => state.scrollToTop(),
    onScrollToBottom: () => state.scrollToBottom(),
    sidebarOpen: state.sidebarOpen,
    sidebarContent: state.sidebarContent,
    sidebarError: state.sidebarError,
    splitRatio: state.splitRatio,
    onOpenSidebar: (content: string) => state.handleOpenSidebar(content),
    onCloseSidebar: () => state.handleCloseSidebar(),
    onOpenMessageViewer: (markdown: string, messageId: string) =>
      state.handleOpenMessageViewer(markdown, messageId),
    onSplitRatioChange: (ratio: number) => state.handleSplitRatioChange(ratio),
    assistantName: state.assistantName,
    assistantAvatar: state.assistantAvatar,
    chatSubagents: state.chatSubagents,
    sessionBackgroundRunIds: state.sessionBackgroundRunIds,
    fleetSessionActivity: state.fleetSessionActivity,
    onActivateFleetPlanUnit: (childSessionKey) => state.activateChatSession(childSessionKey),
    // UX-PLAN Pillar D — feed the inspector + thread from the same snapshot.
    chatSessionSnapshot: sessionSnapshot,
    chatSubagentExpanded: state.chatSubagentExpanded,
    onToggleSubagentExpand: (id) => state.handleToggleSubagentExpand(id),
    // JAISIU-XXXX — per-session "I opened this finished run" flag; only
    // populated for terminal cards so the chat is never dominated by a
    // long list of expanded completed runs.
    chatSubagentTerminalOpen: state.chatSubagentTerminalOpen,
    onToggleTerminalSubagentOpen: (id) => state.handleToggleTerminalSubagentOpen(id),
    onCancelSubagentRun: (card) => state.handleCancelSubagentRun(card),
    onRetrySubagentRun: (card) => state.handleRetrySubagentRun(card),
    onCancelAllSubagents: (opts) => state.handleCancelAllSubagents(opts),
    onSynthesizeFanIn: (opts) => state.handleSynthesizeFanIn(opts),
    fanInSynthesis: state.fanInSynthesisBySession[state.sessionKey] ?? { state: "none" },
    fanInPending: state.fanInPendingBySession[state.sessionKey],
    fanInError: state.fanInErrorBySession[state.sessionKey],
    chatSubagentPendingActions: state.chatSubagentPendingActions,
    chatSubagentFilter: state.chatSubagentFilter,
    onSubagentFilterChange: (next) => state.handleSubagentFilterChange(next),
    onCollapseAllSubagents: () => state.handleCollapseAllSubagents(),
    onJumpLatestRunning: () => state.handleJumpLatestRunningSubagent(),
    // Parent → children hierarchy: state + handler for the
    // "Focus children" toggle on a parent subagent card.
    selectedSubagentParentId: state.selectedSubagentParentId,
    onSelectSubagentParent: (parentId) => state.handleSelectSubagentParent(parentId),
    onFleetGraphEditRoutes: () => state.handleFleetGraphEditRoutes(),
    onEditAgentConfig: (id) => state.handleEditAgentConfigFromFleet(id),
    onOpenAgentsTab: (id) => state.handleOpenAgentsTabForAgent(id),
    fleetChatMainTab: state.settings.fleetChatMainTab,
    localLlm: buildLocalLlmViewProps(state),
    onFleetChatMainTabChange: (tab) => {
      // JAISIU: Local LLM is reachable from the left main menu only —
      // don't yank the inspector away from whatever the operator is
      // looking at. For other main tabs, drive the inspector as before.
      //
      // JAISIU-3603 followup (operator bug 2026-09-28): the chat-history
      // sidebar calls this handler with tab === "chat" whenever the
      // operator picks another session from the list. Forcing
      // fleetWorkspacePreset = "operator" on that path silently
      // switched the layout from "unified" to "operator", and the
      // operator preset's inspector panel does not honor the
      // fleetUnifiedDrawerOpen toggle — so the sidebar appeared
      // "always opened" and the topbar Inspector toggle had no
      // visible effect after the first chat switch. Skip the preset
      // override when the destination tab is "chat" (a navigation,
      // not a layout change).
      const inspectorTab =
        tab === "localLlm" ? state.settings.fleetInspectorTab : fleetMainTabToInspectorTab(tab);
      const nextSettings: typeof state.settings = {
        ...state.settings,
        fleetChatMainTab: tab,
        fleetInspectorTab: inspectorTab,
      };
      if (tab !== "chat") {
        nextSettings.fleetWorkspacePreset = "operator";
      }
      state.applySettings(nextSettings);
      syncLocalLlmPolling(state as unknown as Parameters<typeof syncLocalLlmPolling>[0]);
      if (tab === "localLlm" && state.connected) {
        void refreshLocalLlm(state);
      }
      if (tab === "files") {
        state.handleSessionEnvironmentOpenWorkspace();
        void state.refreshChatWorkspace();
      }
    },
    fleetWorkspacePanelOpen: !state.settings.chatPanelsCollapsed.workspace,
    onToggleFleetWorkspacePanel: () => {
      const nextCollapsed = !state.settings.chatPanelsCollapsed.workspace;
      state.applySettings({
        ...state.settings,
        chatPanelsCollapsed: {
          ...state.settings.chatPanelsCollapsed,
          workspace: nextCollapsed,
        },
      });
      if (!nextCollapsed && state.connected) {
        void state.refreshChatWorkspace();
      }
    },
    onFocusFleetProjectFiles: () => {
      state.handleSessionEnvironmentOpenWorkspace();
      void state.refreshChatWorkspace();
    },
    onOpenLocalLlmConsole: () => {
      openLocalLlmInFleet(state as unknown as Parameters<typeof openLocalLlmInFleet>[0]);
    },
    fleetSessionChromeCollapsed: state.settings.fleetSessionChromeCollapsed,
    onToggleFleetSessionChromeCollapsed: () => {
      state.applySettings({
        ...state.settings,
        fleetSessionChromeCollapsed: !state.settings.fleetSessionChromeCollapsed,
      });
    },
    fleetSessionNavigatorScrollable: state.settings.fleetSessionNavigatorScrollable,
    onToggleFleetSessionNavigatorScrollable: () => {
      state.applySettings({
        ...state.settings,
        fleetSessionNavigatorScrollable: !state.settings.fleetSessionNavigatorScrollable,
      });
    },
    sessionNavigatorCanBack: app.canSessionNavigatorBack(),
    sessionNavigatorCanForward: app.canSessionNavigatorForward(),
    onSessionNavigatorBack: () => app.sessionNavigatorBack(),
    onSessionNavigatorForward: () => app.sessionNavigatorForward(),
    fleetChatComposerCollapsed: state.settings.fleetChatComposerCollapsed,
    onToggleFleetChatComposerCollapsed: () => {
      state.applySettings({
        ...state.settings,
        fleetChatComposerCollapsed: !state.settings.fleetChatComposerCollapsed,
      });
    },
    onOpenFleetGuide: () => state.handleOpenFleetGuide(),
    onNavigateSetup: (tab) => state.setTab(tab),
    onDeleteChatSession: (key) => void state.handleDeleteChatSession(key),
    onManageSessions: () => state.handleManageSessions(),
    onShowArchivedSessions: () => state.handleShowArchivedSessions(),
    onNewAgent: () => state.handleNewAgentFromChat(),
    onOpenAgents: () => state.handleOpenAgentsFromChat(),
    onOpenSubagentSession: (params) => state.openSubagentChatTab(params),
    subagentCardHost: app,
    onReturnToParentSession: (parentKey) => state.activateChatSession(parentKey),
    chatTabMeta: state.chatTabMeta,
    chatRunId: state.chatRunId,
    chatTabHighlightKey: state.chatTabHighlightKey,
    multiAgentHintDismissed: state.settings.multiAgentHintDismissed,
    onMultiAgentHintDismiss: () => {
      state.applySettings({
        ...state.settings,
        multiAgentHintDismissed: true,
      });
    },
    onDraftChangeForMultiAgent: (next) => {
      state.chatMessage = next;
      state.chatMentionIndex = 0;
      requestAnimationFrame(() => {
        syncChatComposeDraft(next);
        document.querySelector<HTMLTextAreaElement>(".chat-compose--fleet textarea")?.focus();
      });
    },
    agentCount: state.agentsList?.agents?.length ?? 0,
    fleetProjectsLoading: state.fleetProjectsLoading,
    fleetProjectsError: state.fleetProjectsError,
    fleetProjects: state.fleetProjects,
    chatActiveProjectId: state.chatActiveProjectId,
    onFleetProjectsRefresh: () => void state.refreshFleetProjects(),
    onFleetProjectsDiscover: () => void state.handleDiscoverFleetProjects(),
    onFleetProjectCreate: (name) => void state.handleCreateFleetProject(name),
    onFleetProjectOpen: (project) => void state.handleOpenFleetProject(project),
    onFleetProjectSelectForFiles: (project) => void state.handleSelectFleetProjectForFiles(project),
    onFleetProjectRename: (project, name) => void state.handleRenameFleetProject(project, name),
    onFleetProjectDelete: (project) => void state.handleDeleteFleetProject(project),
    chatWorkspaceLoading: state.chatWorkspaceLoading,
    chatWorkspaceError: state.chatWorkspaceError,
    chatWorkspacePath: resolveDisplayedChatWorkspacePath({
      selfImprovement: state.chatSendSelfImprovement,
      repoWorkspace: state.chatSelfImprovementRepoWorkspace,
      sessionWorkspace: state.chatWorkspacePath,
    }),
    chatWorkspaceScope: state.chatWorkspaceScope,
    chatWorkspaceTruncated: state.chatWorkspaceTruncated,
    chatWorkspaceTree: state.chatWorkspaceTree,
    chatWorkspaceExpandedDirs: state.chatWorkspaceExpandedDirs,
    chatWorkspaceSelectedPath: state.chatWorkspaceSelectedPath,
    chatWorkspaceFileLoading: state.chatWorkspaceFileLoading,
    chatWorkspaceFileError: state.chatWorkspaceFileError,
    chatWorkspaceFileContent: state.chatWorkspaceFileContent,
    chatWorkspaceFileTruncated: state.chatWorkspaceFileTruncated,
    onChatWorkspaceRefresh: () => void state.refreshChatWorkspace(),
    onChatWorkspaceToggleDir: (path) => state.toggleChatWorkspaceDir(path),
    onChatWorkspaceOpenFile: (path) => void state.openChatWorkspaceFile(path),
    sessionArtifacts: state.sessionArtifacts,
    sessionArtifactsLoading: state.sessionArtifactsLoading,
    sessionArtifactsError: state.sessionArtifactsError,
    sessionArtifactSelectedId: state.sessionArtifactSelectedId,
    sessionArtifactDetail: state.sessionArtifactDetail,
    sessionArtifactDetailLoading: state.sessionArtifactDetailLoading,
    sessionArtifactEditContent: state.sessionArtifactEditContent,
    onRefreshSessionArtifacts: () => void state.refreshSessionArtifacts(),
    onSelectSessionArtifact: (id) => state.handleSelectSessionArtifact(id),
    onSessionArtifactEditContentChange: (content) => {
      state.sessionArtifactEditContent = content;
    },
    onSaveSessionWorkspaceFile: () => void state.handleSessionInspectorSaveWorkspaceFile(),
    queueServiceStatus: state.queueServiceStatus,
    queueServiceLoading: state.queueServiceLoading,
    queueServiceError: state.queueServiceError,
    queueRestartLoading: state.queueRestartLoading,
    queueRestartError: state.queueRestartError,
    onRefreshQueueService: () => void state.loadQueueServiceStatus(),
    onRestartQueueService: () => void state.restartQueueService(),
    onDelegationEditResponses: (key) => state.handleSessionInspectorEditResponses(key),
    onDelegationEditSandboxFiles: (key) => state.handleSessionInspectorEditSandboxFiles(key),
    chatActivityTab: state.chatActivityTab,
    onChatActivityTabChange: (tab) => state.handleChatActivityTab(tab),
    chatActivitySearchQuery: state.chatActivitySearchQuery,
    onChatActivitySearchChange: (query) => state.handleChatActivitySearch(query),
    fleetV2AnalyticsTab: state.fleetV2AnalyticsTab,
    onFleetV2AnalyticsTabChange: (tab) => state.handleFleetV2AnalyticsTab(tab),
    // Per-session gateway events for the right-side Inspector Activity tab.
    // Falls back to a flat tail when the host hasn't indexed by session yet.
    gatewayEventsBySession: state.getGatewayEventsBySession?.() ?? {},
    gatewayEvents: state.gatewayEventTail ?? [],
    eventsScopeSessionKey: state.sessionKey,
    // Fleet-wide subagent aggregation for the right-side Subagents tab.
    // Excludes the active session (already rendered as `chatSubagents`).
    otherSubagentBuckets: state.getOtherSubagentBuckets?.() ?? [],
    fleetV2DirectoryTab: state.fleetV2DirectoryTab,
    onFleetV2DirectoryTabChange: (tab) => state.handleFleetV2DirectoryTab(tab),
    fleetResetNodeBusy: state.fleetResetNodeBusy,
    onFleetResetNode: () => void state.handleFleetResetNode(),
    sessionTabActivity: state.sessionTabActivity,
    fleetChatHistoryCollapsed: state.settings.fleetChatHistoryCollapsed,
    // JAISIU-4497 — the chat history sidebar builds its "open in a new
    // browser tab" deep link from the deployment's base path.
    basePath: state.basePath,
    onToggleFleetChatHistoryCollapsed: () => {
      state.applySettings({
        ...state.settings,
        fleetChatHistoryCollapsed: !state.settings.fleetChatHistoryCollapsed,
      });
    },
    chatHistorySearchQuery: app.chatHistorySearchQuery,
    // JAISIU-3655 (followup) — operator toggle for sidebar row
    // filter (default OFF). Surfaces through the gear-icon
    // popover next to the × in the sidebar search input.
    chatHistorySearchInActivities: app.chatHistorySearchInActivities === true,
    onChatHistorySearchInActivitiesChange: (next: boolean) => {
      // Shared with the in-thread toggle (same host state).
      app.chatHistorySearchInActivities = next;
      app.chatMessageSearchInActivities = next;
    },
    onChatHistorySearchQueryChange: (next) => {
      app.chatHistorySearchQuery = next;
      // JAISIU-3651 (followup) — mirror the sidebar query into
      // the in-thread highlight state so the currently-open
      // chat's messages get the same phrase highlighted as the
      // sidebar rows. `handleChatMessageSearchQueryChange`
      // updates `chatMessageSearchQuery`, captures the read
      // anchor on the empty→non-empty transition, resets the
      // counter, and (on clear) wipes existing marks — all of
      // which are the right behaviours when the operator is
      // driving the highlight from the sidebar instead of the
      // chat-thread input. The reverse direction (chat-thread
      // input → sidebar) is intentionally NOT mirrored: typing
      // in the chat-thread box should not silently filter the
      // entire chat history list.
      app.handleChatMessageSearchQueryChange(next);
      // JAISIU-3651 (followup) — also kick the deep-batch walker
      // for the currently-open chat so older (off-screen) matches
      // are counted too. The chat-thread input path kicks the same
      // walker from its `oninput` event (line ~415 below); when the
      // sidebar is the source the programmatic `.value` write to
      // `chatMessageSearchQuery` does NOT fire `input`, so we
      // trigger the walker here directly. Without this, a sidebar
      // query against an older chat with the first match deep in
      // history would show "0 / 0" in the in-thread counter and
      // leave the matching rows un-highlighted until the operator
      // paged older messages manually.
      if (next.replace(/\s+/g, "").length > 0) {
        void searchActiveChatDeepBatch(app, next).then((result) => {
          app.handleChatMessageDeepBatchResult(result);
        });
      }
      // JAISIU-3651 — kick off a quiet history load for any chat that
      // already matches on title/preview in the current catalog but
      // does NOT yet have a body batch in the local cache, so body
      // search hits become visible as soon as the chunk loads. This
      // runs once on every keystroke; we cap concurrency at 4
      // in-flight RPCs so a fast typist never queues dozens of
      // parallel requests.
      if (next.trim()) {
        void loadMissingChatBatchesForSidebarSearch(app, next, sidebarSearchCandidates);
      }
    },
    // JAISIU-3651 — when the operator types in the sidebar search,
    // forward a per-session body-text index so loaded message bodies
    // also count as a match. Built lazily on each render via the
    // accessor on `app.chatMessagesBySession`; the sidebar skips the
    // index on the empty-query fast path so the default state stays
    // zero-cost.
    chatHistoryLoadedMessageIndex: app.chatHistorySearchQuery.trim()
      ? buildSidebarLoadedMessageTextIndex(app.getChatMessagesBySessionForSidebar())
      : undefined,
    // JAISIU-3327 / UX-R2 — bulk-select state is local to the App so it
    // survives sidebar re-renders and refreshes. State mutations route
    // through the `chat-history-bulk-select` controller so the rules
    // (reset on exit, dedupe, empty-blank-key guard) stay in one place.
    chatHistorySelectMode: app.chatHistorySelectMode,
    chatHistorySelectedKeys: app.chatHistorySelectedKeys,
    chatHistoryAllSelectableKeys: app.chatHistoryAllSelectableKeys,
    onToggleChatHistorySelectMode: () => applyToggleSelectMode(app),
    onToggleChatHistoryKeySelected: (key) => applyToggleKeySelected(app, key),
    onSelectAllChatHistoryKeys: () => applySelectAllKeys(app, app.chatHistoryAllSelectableKeys),
    onClearChatHistorySelection: () => applyClearSelection(app),
    onBulkDeleteSelectedChatHistory: () => app.handleBulkDeleteSelectedChatSessions(),
    onBulkArchiveSelectedChatHistory: () => app.handleBulkArchiveSelectedChatSessions(),
    // JAISIU-3328 / UX-R3 — favorite (pinned) chats. Hydrated from
    // storage on first construction and updated through toggleFavoriteChat
    // so the localStorage payload stays in sync without a re-render loop.
    chatHistoryFavoriteKeys: app.chatHistoryFavoriteKeys,
    onToggleChatHistoryFavorite: (key) => {
      toggleFavoriteChat(key);
      app.chatHistoryFavoriteKeys = new Set(loadFavoriteChats());
    },
    chatHistoryShowArchived: app.chatHistoryShowArchived,
    onToggleChatHistoryShowArchived: () => {
      app.chatHistoryShowArchived = !app.chatHistoryShowArchived;
    },
    // JAISIU-3500: chat preview visibility (off by default).
    chatHistoryShowPreviews: app.chatHistoryShowPreviews,
    onToggleChatHistoryShowPreviews: () => {
      app.chatHistoryShowPreviews = !app.chatHistoryShowPreviews;
    },
    chatHistoryGroups: history.active,
    chatHistoryArchived: history.archived,
    // Only surface the loading placeholder when the catalog is still empty.
    // Background poll/refresh must not replace an existing list with "Loading…".
    //
    // `sessionsLoading` alone is NOT this signal: the chat-history catalog
    // runs `quiet: true`, which only sets `sessionsPolling`, so the flag was
    // permanently false here and the sidebar painted "No chats yet. Start a
    // new chat." over a live catalog on every first paint and reload.
    // `sessionsCatalogSettled` is what tells us no fetch has completed yet —
    // and unlike `sessionsResult === null` it also settles when the fetch
    // throws, so this cannot spin "Loading…" forever on a dead gateway.
    chatHistoryLoading: !state.sessionsResult && !(state.sessionsCatalogSettled ?? false),
    chatHistoryCanArchiveActive: canArchiveSessionKey(state.sessionKey),
    onChatHistoryRefresh: () => void app.refreshChatHistorySessions(),
    onChatHistoryNewChat: () => void app.handleNewEmptyChatTab(),
    onChatHistoryArchiveAndNew: () => void app.handleArchiveAndNewChat(),
    onChatHistorySelectSession: (key, opts) => app.handleOpenChatHistorySession(key, opts),
    resolveChatHistoryJumpToMessageId: (key) => app.resolveSidebarJumpToMessageId(key),
    onChatHistoryArchiveSession: (key) => app.handleArchiveChatSession(key),
    onChatHistoryRestoreSession: (key) => app.handleRestoreArchivedChat(key),
    onChatHistoryDeleteSession: (key) => void app.handleDeleteChatSession(key),
    onChatHistoryRenameSession: (key, title) => void app.handleRenameChatSession(key, title),
    chatContextPanelOpen: state.chatContextPanelOpen,
    chatContextBreakdownLoading: state.chatContextBreakdownLoading,
    chatContextBreakdownError: state.chatContextBreakdownError,
    chatContextBreakdown: state.chatContextBreakdown,
    onOpenChatContextPanel: () => app.handleOpenChatContextPanel(),
    onCloseChatContextPanel: () => app.handleCloseChatContextPanel(),
    onRefreshChatContextBreakdown: () => app.handleRefreshChatContextBreakdown(),
    chatPalaceContinuityOpen: state.chatPalaceContinuityOpen,
    chatPalaceContinuityLoading: state.chatPalaceContinuityLoading,
    chatPalaceContinuityClearing: state.chatPalaceContinuityClearing,
    chatPalaceContinuityMutating: state.chatPalaceContinuityMutating,
    chatPalaceContinuityPending: state.chatPalaceContinuityPending?.size ?? 0,
    chatPalaceContinuityError: state.chatPalaceContinuityError,
    chatPalaceContinuity: state.chatPalaceContinuity,
    chatPalaceContinuityFlash: state.chatPalaceContinuityFlash,
    chatPalaceContinuityDraft: state.chatPalaceContinuityDraft,
    chatPalaceContinuityDraftCategory: state.chatPalaceContinuityDraftCategory,
    onToggleChatPalaceContinuity: () => app.handleToggleChatPalaceContinuity(),
    chatComposerPlusOpen: state.chatComposerPlusOpen,
    chatComposerPlusFocusIndex: state.chatComposerPlusFocusIndex,
    onToggleChatComposerPlus: () => app.handleToggleChatComposerPlus(),
    onCloseChatComposerPlus: () => app.handleCloseChatComposerPlus(),
    onSetChatComposerPlusFocusIndex: (next: number) =>
      app.handleSetChatComposerPlusFocusIndex(next),
    onCloseChatPalaceContinuity: () => app.handleCloseChatPalaceContinuity(),
    onRefreshChatPalaceContinuity: () => app.handleRefreshChatPalaceContinuity(),
    onClearChatPalaceContinuity: () => app.handleClearChatPalaceContinuity(),
    onRemoveChatPalaceContinuityPoint: (pointId: string) =>
      app.handleRemoveChatPalaceContinuityPoint(pointId),
    onChatPalaceContinuityDraftChange: (draft: string) =>
      app.handleChatPalaceContinuityDraftChange(draft),
    onChatPalaceContinuityDraftCategoryChange: (category: "fact" | "decision") =>
      app.handleChatPalaceContinuityDraftCategoryChange(category),
    onAddChatPalaceContinuityPoint: () => app.handleAddChatPalaceContinuityPoint(),
    fleetWorkspacePreset: state.settings.fleetWorkspacePreset,
    fleetWorkspaceLayout: state.settings.fleetWorkspaceLayouts[state.settings.fleetWorkspacePreset],
    fleetWorkspaceDensity: state.settings.fleetWorkspaceDensity,
    fleetInspectorTab: state.settings.fleetInspectorTab,
    fleetExecutionExpandedId: state.fleetExecutionExpandedId,
    onFleetExecutionToggle: (id) => {
      state.fleetExecutionExpandedId = state.fleetExecutionExpandedId === id ? null : id;
    },
    // Per-session, per-host tunnel health — surface in the inspector's
    // "Tunnels" tab. The map is built by the `TunnelHealthController`
    // in `controllers/tunnel-health.ts` and refreshed whenever the
    // manager emits a state change.
    tunnelRecords: state.tunnelRecords,
    onTunnelReconnect: (key) => state.handleTunnelReconnect?.(key),
    onTunnelForget: (key) => state.handleTunnelForget?.(key),
    fleetUnifiedDrawerTab: state.settings.fleetUnifiedDrawerTab,
    onFleetUnifiedDrawerTabChange: (tab) => {
      state.applySettings({
        ...state.settings,
        fleetUnifiedDrawerTab: tab,
      });
      if (tab === "files") {
        state.handleSessionEnvironmentOpenWorkspace();
        void state.refreshChatWorkspace();
      }
    },
    fleetUnifiedDrawerOpen: state.settings.fleetUnifiedDrawerOpen,
    onFleetUnifiedDrawerToggle: () => {
      state.applySettings({
        ...state.settings,
        fleetUnifiedDrawerOpen: !state.settings.fleetUnifiedDrawerOpen,
      });
    },
    onFleetWorkspacePresetChange: (preset: FleetWorkspacePreset) => {
      state.applySettings({
        ...state.settings,
        fleetWorkspacePreset: preset,
      });
    },
    onFleetWorkspaceLayoutChange: (layout: FleetWorkspaceLayoutState) => {
      const preset = state.settings.fleetWorkspacePreset;
      state.applySettings({
        ...state.settings,
        fleetWorkspaceLayouts: {
          ...state.settings.fleetWorkspaceLayouts,
          [preset]: layout,
        },
      });
    },
    onFleetWorkspaceDensityChange: (density) => {
      state.applySettings({
        ...state.settings,
        fleetWorkspaceDensity: density,
      });
    },
    onFleetInspectorTabChange: (tab: FleetInspectorTabId) => {
      state.applySettings({
        ...state.settings,
        fleetInspectorTab: tab,
      });
      syncLocalLlmPolling(state as unknown as Parameters<typeof syncLocalLlmPolling>[0]);
      // Per-tab refresh hooks — mirror handleSessionInspectorTab for the drawer
      // (ui/src/ui/app.ts:handleSessionInspectorTab) so the always-visible right
      // inspector panel does not paint stale/empty state on tab change. Each
      // branch is a no-op if the host lacks the corresponding data hook.
      const h = state as unknown as {
        refreshSessionArtifacts?: () => Promise<void> | void;
        refreshChatWorkspace?: () => Promise<void> | void;
        refreshSessionInspectorBackground?: () => Promise<void> | void;
        refreshQueueService?: () => Promise<void> | void;
        refreshLocalLlmHost?: () => Promise<void> | void;
      };
      if (tab === "files") {
        void h.refreshSessionArtifacts?.();
        void h.refreshChatWorkspace?.();
      } else if (tab === "subagents") {
        // No network fetch needed; restoreChatSubagentsForSession in
        // activateChatSession already merged catalog + stash. Re-call it
        // synchronously to pick up subagent rows that arrived while
        // another tab was active.
        state.chatSubagents = restoreChatSubagentsForSession(
          state as unknown as Parameters<typeof restoreChatSubagentsForSession>[0],
          state.sessionKey,
          state.sessionsResult,
        );
      }
    },
    // JAISIU-3081: persisted inspector panel width.
    fleetInspectorWidth: state.settings.inspectorWidth,
    onFleetInspectorResize: (nextWidthPx: number) => {
      // JAISIU-3082: nextWidthPx is now an index 0..2 into
      // INSPECTOR_WIDTH_PRESETS (was raw pixels in JAISIU-3081). The
      // tab-specific side effects from the JAISIU-3081 drag-to-resize
      // handler were removed because they referenced an out-of-scope
      // `tab` and they don't apply to a discrete width pick — refresh
      // hooks live on the per-tab change handlers (e.g.
      // onFleetInspectorTabChange).
      state.applySettings({
        ...state.settings,
        inspectorWidth: nextWidthPx,
      });
    },
    onFleetWorkspacePanelVisibilityChange: (panel, visible) => {
      const preset = state.settings.fleetWorkspacePreset;
      const current = state.settings.fleetWorkspaceLayouts[preset];
      state.applySettings({
        ...state.settings,
        fleetWorkspaceLayouts: {
          ...state.settings.fleetWorkspaceLayouts,
          [preset]: {
            ...current,
            visiblePanels: {
              ...current.visiblePanels,
              [panel]: visible,
            },
          },
        },
      });
    },
    onFleetWorkspaceLayoutReset: () => {
      const preset = state.settings.fleetWorkspacePreset;
      state.applySettings({
        ...state.settings,
        fleetWorkspaceLayouts: {
          ...state.settings.fleetWorkspaceLayouts,
          [preset]: resetFleetWorkspaceLayout(preset),
        },
      });
    },
    environment: {
      loading: state.sessionEnvironmentLoading,
      error: state.sessionEnvironmentError,
      data: state.sessionEnvironment,
      onRefresh: () => {
        void loadSessionEnvironment(state as unknown as SessionEnvironmentState);
      },
    },
    operatorTrustSession: resolveOperatorTrustForSession(state.operatorTrustBySession, sessionKey),
    operatorTrustLastSignalAt: state.operatorTrustLastSignalAt,
    fleetStats: state.fleetStats,
  };
}

export function isFleetConsoleView(tab: AppViewState["tab"]): boolean {
  return isFleetConsoleTab(tab);
}
